import destinationNavigationData from 'src/common/components/DestinationNavigation/data.json';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { useUserContext, type UserRole } from 'src/common/providers/UserProvider';
import { EXTERNAL_PATHS, PATHS } from '../../constants';
import ChargeNavItem from './ChargeNavItem';
import CurrencySelectorNavItem from './CurrencySelectorNavItem';
import InboxNavItem from './InboxNavItem';
import MyListingsNavItem from './MyListingsNavItem';
import SubNavHeading from './SubNavHeading';
import type {
  MainNavItemComponentType, MainNavItemLinkType,
  MainNavItemType,
  NavItemComponentType, NavItemLinkType,
  NavItemType,
} from './types';
import AuthNavItem from './AuthNavItem';
import LanguageSelectorNavItem from './LanguageSelectorNavItem';
import LogoutNavItem from './LogoutNavItem';
import SubNavUserDetails from './SubNavUserDetails';

const charge: MainNavItemComponentType = {
  key: 'charge',
  component: <ChargeNavItem />,
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on extra small screens for owners
    ownerAdmin: ['m'],
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    ownerAdmin: ['l'],
  },
};
const myListings: MainNavItemComponentType = {
  key: 'my-listings',
  component: <MyListingsNavItem />,
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'],
    ownerAdmin: ['m'],
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    ownerAdmin: ['l'],
  },
};
export const directBooking: NavItemLinkType = {
  i18nKey: 'direct-booking',
  path: PATHS.CREATE_DIRECT_BOOKING,
  icon: 'credit-card-arrow',
  roles: ['owner'],
};
export const addOn: NavItemLinkType = {
  i18nKey: 'add-on-payment',
  path: PATHS.CHARGE_ADD_ON,
  icon: 'credit-card-check',
  roles: ['owner'],
  sectionDivider: true,
};
const inbox: MainNavItemComponentType = {
  key: 'inbox',
  component: <InboxNavItem />,
  roles: ['owner', 'renter'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on small screens for owners
    renter: ['xs'], // Shown on medium screens for renters
    ownerAdmin: ['m'], // Special case for owner admin
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    renter: ['m'],
    ownerAdmin: ['l'],
  },
};
const inboxSubNav: NavItemLinkType = {
  i18nKey: 'inbox',
  path: EXTERNAL_PATHS.INBOX,
  icon: 'envelope',
  roles: ['owner', 'renter'],
};
const calendar: MainNavItemLinkType = {
  i18nKey: 'calendar',
  path: EXTERNAL_PATHS.CALENDAR,
  icon: 'calendar',
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on large screens for owners
    ownerAdmin: ['m'],
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    ownerAdmin: ['l'],
  },
};
const ownerAdmin: MainNavItemLinkType = {
  i18nKey: 'admin',
  path: PATHS.ADMIN_LISTINGS,
  icon: 'admin',
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['s'], // Shown on extra large screens for owners
    ownerAdmin: ['m'],
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    ownerAdmin: ['l'],
  },
};
const favorites: NavItemLinkType = {
  i18nKey: 'favorites',
  path: PATHS.FAVORITES,
  icon: 'heart',
  sectionDivider: true,
  roles: ['captain', 'owner', 'renter'],
};
const listYourBoat: NavItemLinkType = {
  i18nKey: 'list-your-boat',
  path: PATHS.ADMIN_LISTINGS,
  icon: 'boat-add',
  roles: ['captain', 'renter', 'anonymous'],
};
const accountCaptainOwner: NavItemLinkType = {
  i18nKey: 'account',
  path: PATHS.ADMIN_ACCOUNT,
  icon: 'admin',
  roles: ['captain', 'owner'],
};
const accountRenter: NavItemLinkType = {
  i18nKey: 'account',
  path: PATHS.ADMIN_PROFILE,
  icon: 'gear',
  roles: ['renter'],
};
const help: NavItemLinkType = {
  i18nKey: 'help',
  path: 'https://getmyboat.zendesk.com/',
  openInNewTab: true,
  icon: 'question',
  sectionDivider: true,
  roles: ['owner', 'captain', 'renter', 'anonymous'],
};
const createAccount: MainNavItemLinkType = {
  i18nKey: 'create-account',
  icon: 'profile',
  path: EXTERNAL_PATHS.REGISTER,
  roles: ['anonymous'],
  isHeaderNavItem: {
    anonymous: ['m'],
  },
};
const login: MainNavItemLinkType = {
  i18nKey: 'login',
  icon: 'login',
  path: EXTERNAL_PATHS.LOGIN,
  roles: ['anonymous'],
  isHeaderNavItem: {
    anonymous: ['xl'],
  },
};
const currencySelector: NavItemComponentType = {
  key: 'currency-selector',
  component: <CurrencySelectorNavItem />,
};
const chargeSubNav = [
  {
    key: 'charge-heading',
    component: <SubNavHeading i18nKey="charge" />,
  },
  directBooking,
  addOn,
];
const languageSelector = {
  key: 'language-selector',
  component: <LanguageSelectorNavItem />,
};

const myListingsSubNav: NavItemType[] = [
  {
    key: 'my-listings-heading',
    component: <SubNavHeading i18nKey="my-listings" />,
  },
  {
    i18nKey: 'my-listings--boats',
    path: PATHS.ADMIN_LISTINGS,
    icon: 'boat',
    roles: ['owner'],
  },
  {
    i18nKey: 'my-listings--add-ons',
    path: PATHS.ADMIN_ADDONS,
    icon: 'hand-coins',
    roles: ['owner'],
    sectionDivider: true,
  },
];

const useNavigationConfig = (): (MainNavItemType | NavItemType)[] => {
  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const adminOrMyListings = isNavUpdateEnabled ? myListings : ownerAdmin;

  return [
    charge,
    inbox,
    calendar,
    adminOrMyListings,
    favorites,
    listYourBoat,
    accountCaptainOwner,
    accountRenter,
    help,
    {
      ...createAccount,
      path: isAuthMigrationEnabled ? PATHS.REGISTER : EXTERNAL_PATHS.REGISTER,
    },
    {
      ...login,
      path: isAuthMigrationEnabled ? PATHS.LOGIN : EXTERNAL_PATHS.LOGIN,
    },
  ];
};

const renterConfigItemsOrdered: NavItemType[] = [
  inboxSubNav,
  {
    ...favorites,
    sectionDivider: false,
  },
  accountRenter,
  {
    ...listYourBoat,
    sectionDivider: true,
  },
  currencySelector,
  help,
];

const captainConfigItemsOrdered: NavItemType[] = [
  inboxSubNav,
  {
    ...favorites,
    sectionDivider: false,
  },
  accountCaptainOwner,
  {
    ...listYourBoat,
    sectionDivider: true,
  },
  currencySelector,
  help,
];

const getAnonymousSubNavItems = (isAuthMigrationEnabled?: boolean): NavItemType[] => [
  listYourBoat,
  { ...help, sectionDivider: true },
  {
    ...createAccount,
    path: isAuthMigrationEnabled ? PATHS.REGISTER : EXTERNAL_PATHS.REGISTER,
  },
  {
    ...login,
    path: isAuthMigrationEnabled ? PATHS.LOGIN : EXTERNAL_PATHS.LOGIN,
  },
];

export const getOwnerSubNavConfig = (isNavUpdateEnabled?: boolean): NavItemType[] => [
  ...chargeSubNav,
  inboxSubNav,
  calendar,
  ...(isNavUpdateEnabled ? [] : [ownerAdmin]),
  favorites,
  ...(isNavUpdateEnabled ? myListingsSubNav : []),
  ...(isNavUpdateEnabled ? [accountCaptainOwner] : []),
  currencySelector,
  help,
];

// Helper to get authed menu items based on role and feature flags
export const getAuthenticatedSubNavItems = (
  userRole: UserRole,
  isNavUpdateEnabled?: boolean,
): NavItemType[] => {
  switch (userRole) {
    case 'captain':
      return captainConfigItemsOrdered;
    case 'owner':
      return getOwnerSubNavConfig(isNavUpdateEnabled);
    case 'renter':
      return renterConfigItemsOrdered;
    default:
      return [];
  }
};

const getNestedAuthMenuItems = (
  userRole: UserRole,
  isNavUpdateEnabled?: boolean,
): NavItemType[] => [
  {
    i18nKey: 'root',
    children: [
      {
        key: 'user-details',
        component: <SubNavUserDetails />,
        sectionDivider: true,
      },
      ...(getAuthenticatedSubNavItems(userRole, isNavUpdateEnabled)),
      {
        key: 'logout',
        component: <LogoutNavItem />,
      },
    ],
  },
];

const getNestedAnonymousMenuItems = (
  anonymousMenuItems: NavItemType[],
  nestedMenuVariant: 'sidebar' | undefined,
): NavItemType[] => {
  const authForSidebar = nestedMenuVariant === 'sidebar' ? [{
    key: 'auth',
    component: <AuthNavItem />,
    sectionDivider: true,
  }] : [];

  return [
    {
      i18nKey: '',
      children: [
        ...authForSidebar,
        ...destinationNavigationData,
        languageSelector,
        {
          ...currencySelector,
          sectionDivider: nestedMenuVariant !== 'sidebar',
        },
        ...anonymousMenuItems.filter((item) => {
          if (nestedMenuVariant === 'sidebar') {
            if (item.key === 'create-account' || item.key === 'login') {
              return false;
            }
          }
          return true;
        }),
      ],
    },
  ];
};

export const useSubNavigationConfig = (
  nestedMenuVariant: 'sidebar' | undefined,
): NavItemType[] => {
  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const { user, userRole } = useUserContext();
  const subNavItems = getAnonymousSubNavItems(isAuthMigrationEnabled);

  if (!user?.id) {
    return getNestedAnonymousMenuItems(subNavItems, nestedMenuVariant);
  }

  return getNestedAuthMenuItems(userRole, isNavUpdateEnabled);
};

export default useNavigationConfig;
